"use client";

import React from "react";
import { ArrowRight, Compass, Move, ShieldAlert } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface ConsiderationCard {
  id: string;
  title: string;
  body: string;
  icon: React.ElementType;
}
const considerations: ConsiderationCard[] = [{
  id: "card-permanent",
  title: "Permanent Venue",
  body: "Measure the intended operating area before purchasing and compare it with the documented dimensions for the complete system.",
  icon: Compass
}, {
  id: "card-mobile",
  title: "Mobile Operation",
  body: "Consider both assembled dimensions and packed dimensions because the equipment needs to fit the operating location and your transport arrangement.",
  icon: Move
}, {
  id: "card-access",
  title: "Access",
  body: "Doors, gates, loading areas and storage routes can matter just as much as the final operating footprint.",
  icon: ShieldAlert
}];
export default function SpaceGuide() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="spacerequirementsguide" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6">
        {/* Header Block */}
        <div className="max-w-3xl">
          <p className="font-mono text-xs tracking-wider uppercase text-primary">
            Operating Clearance &amp; Logistics
          </p>
          <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl md:text-5xl">
            How much space does an inflatable mechanical bull need?
          </h2>

          <div className="mt-6 space-y-3 border-l-2 border-primary pl-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            <p className="font-medium text-foreground">
              Space requirements depend on the exact bull and inflatable arena.
              The mechanical base alone does not represent the full operating
              footprint.
            </p>
            <p className="text-sm sm:text-base">
              Before buying, look for the complete assembled dimensions of the
              actual package. In addition, consider access around the setup,
              controller position and any product-specific space requirements.
            </p>
          </div>
        </div>

        {/* 3 Consideration Cards */}
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {considerations.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="h-full">
                <Card className="h-full rounded-none border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md">
                  <CardHeader className="gap-3 pb-3">
                    <div className="inline-flex size-10 items-center justify-center border border-border bg-muted text-foreground">
                      <IconComponent className="size-5 text-primary" />
                    </div>
                    <CardTitle className="font-mono text-sm tracking-wider uppercase text-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {item.body}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Action Button */}
        <div className="mt-12">
          <Button asChild size="lg" className="rounded-none border border-primary bg-primary px-8 font-mono text-xs tracking-wider uppercase text-primary-foreground hover:bg-primary/90">
            <Link to="/SetupSafetyAndMaintenancePage">
              <span>Read setup &amp; safety guide</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}